import { DroneEntity } from '@src/core/features';
import { EMPTY_STRING } from '@src/core/config';
import { SetURLSearchParams } from 'react-router-dom';
import { removeOptimisticMissionSerials, setSelectedDrone } from '@src/features/drone/redux/actions';
import { trustedMissionIdFromDrone } from '@src/features/drone/utils/trusted-mission-id-from-drone';
import type { AppDispatch } from '@src/store';

/** Ignores legacy `?droneSerial=---` deselect values. */
export function dronesPageDroneSerialFromUrl(params: URLSearchParams): string | null {
  const serial = params.get('droneSerial');
  if (!serial || serial === EMPTY_STRING) return null;
  return serial;
}

export type SelectDroneOnMapOptions = {
  /** Drop mission/order from URL (idle selection). */
  clearMissionContext?: boolean;
  missionId?: string;
  orderId?: number | string;
};

function buildSearchParams(drone: DroneEntity, options?: SelectDroneOnMapOptions): URLSearchParams {
  const params = new URLSearchParams();
  params.set('droneSerial', drone.serialNumber);

  if (options?.clearMissionContext) return params;

  const missionId = options?.missionId ?? trustedMissionIdFromDrone(drone);
  if (missionId) params.set('missionId', missionId);

  const orderId = options?.orderId ?? drone.currentOrderId;
  if (orderId != null && orderId !== '') params.set('orderId', String(orderId));

  return params;
}

/** URL is the long-term source of truth for which drone/mission/order is active. */
export function selectDroneOnMap(
  dispatch: AppDispatch,
  setSearchParams: SetURLSearchParams,
  drone: DroneEntity,
  options?: SelectDroneOnMapOptions,
): void {
  if (options?.clearMissionContext) {
    // Selecting this drone as idle: drop only its own optimistic flag, never other drones'.
    dispatch(removeOptimisticMissionSerials([drone.serialNumber]));
  }
  dispatch(setSelectedDrone(drone));
  setSearchParams(buildSearchParams(drone, options), { replace: true });
}

export function clearDroneFromMap(
  dispatch: AppDispatch,
  setSearchParams: SetURLSearchParams,
): void {
  // Keep the optimistic mission serial: deselecting a freshly-missioned drone must not drop it
  // back to idle in the lists. The drones-page cleanup clears it once REST reflects the mission.
  dispatch(setSelectedDrone(null));
  setSearchParams({}, { replace: true });
}
